import { db } from "./firebase";
import { collection, doc, getDocs, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { integrationToFirestore, integrationFromFirestore } from "../models/integration";
import AuditService, { AUDIT_ACTIONS, AUDIT_MODULES } from "./audit_service";
import { COLLECTIONS } from "../services/collections";

/**
 * IntegrationService
 * Centraliza as operações de banco de dados para Integrações externas.
 * Coleção: tenants/{idTenant}/integrations
 */
const IntegrationService = {
    /**
     * Retorna a lista de todas as integrações configuradas para o tenant.
     * Ex: tenants/{idTenant}/integrations/{provider}
     */
    getAll: async (idTenant) => {
        if (!idTenant) throw new Error("ID do tenant não fornecido.");

        const integrationsRef = collection(db, "tenants", idTenant, COLLECTIONS.INTEGRATIONS);
        const snapshot = await getDocs(integrationsRef);

        return snapshot.docs.map(integrationFromFirestore).filter(Boolean);
    },

    /**
     * Busca as configurações de uma integração específica.
     */
    getByProvider: async (idTenant, provider) => {
        if (!idTenant || !provider) {
            throw new Error("ID do tenant e nome do provider são obrigatórios.");
        }

        const docRef = doc(db, "tenants", idTenant, "integrations", provider);
        const snapshot = await getDoc(docRef);

        return integrationFromFirestore(snapshot);
    },

    /**
     * Salva ou atualiza a configuração de uma integração.
     */
    save: async (integrationData, idTenant, authUser) => {
        if (!integrationData.provider) {
            throw new Error("Provider da integração não informado.");
        }

        if (!idTenant) throw new Error("ID do Tenant é obrigatório.");

        const docId = integrationData.id || integrationData.provider;
        const docRef = doc(db, "tenants", idTenant, "integrations", docId);

        const payload = integrationToFirestore(integrationData, { id: idTenant }, authUser);

        await setDoc(docRef, payload, { merge: true });

        // Auditoria
        AuditService.log({
            idTenant,
            user: authUser,
            module: AUDIT_MODULES.INTEGRATIONS,
            action: integrationData.id ? AUDIT_ACTIONS.UPDATE : AUDIT_ACTIONS.CREATE,
            entityId: docId,
            entityName: integrationData.name || integrationData.provider,
            description: `${integrationData.id ? 'Atualizou' : 'Configurou'} a integração "${integrationData.name || integrationData.provider}"`
        });

        return { id: docId, ...payload };
    },

    /**
     * Exclui (ou limpa) as configurações de uma integração.
     */
    delete: async (docId, idTenant, authUser, integrationName) => {
        if (!idTenant || !docId) {
            throw new Error("ID do tenant e ID da integração são obrigatórios para exclusão.");
        }

        const docRef = doc(db, "tenants", idTenant, "integrations", docId);
        await deleteDoc(docRef);

        // Auditoria
        AuditService.log({
            idTenant,
            user: authUser,
            module: AUDIT_MODULES.INTEGRATIONS,
            action: AUDIT_ACTIONS.DELETE,
            entityId: docId,
            entityName: integrationName || docId,
            description: `Excluiu a integração "${integrationName || docId}"`
        });
    },

    /**
     * Extrai uma mensagem de erro segura da resposta da API.
     */
    extractErrorMessage: (data, fallback = "Erro desconhecido.") => {
        if (typeof data?.message === "string") return data.message;
        if (typeof data?.error?.message === "string") return data.error.message;
        if (typeof data?.response?.message === "string") return data.response.message;
        if (Array.isArray(data?.response?.message)) {
            const first = data.response.message[0];
            if (first?.exists === false) return `Número não encontrado no WhatsApp.`;
            return data.response.message.flat().join(", ");
        }
        return fallback;
    },

    /**
     * Testa a conexão/envio com um provider.
     */
    testConnection: async (providerId, config, testInput) => {
        if (providerId === "openai") {
            const res = await fetch("https://api.openai.com/v1/chat/completions", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${config.apiKey}`,
                },
                body: JSON.stringify({
                    model: config.defaultModel || "gpt-4o-mini",
                    messages: [
                        { role: "system", content: config.systemPrompt || "Você é um assistente prestativo." },
                        { role: "user", content: testInput || "Diga uma curiosidade." },
                    ],
                    max_tokens: 200,
                }),
            });
            const data = await res.json();
            return data.error
                ? { success: false, message: data.error.message }
                : { success: true, message: data.choices?.[0]?.message?.content || "Resposta recebida!" };

        } else if (providerId === "gemini") {
            const model = config.defaultModel || "gemini-1.5-flash";
            const body = { contents: [{ parts: [{ text: testInput || "Diga uma curiosidade." }] }] };
            if (config.systemPrompt) body.systemInstruction = { parts: [{ text: config.systemPrompt }] };

            const res = await fetch(
                `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${config.apiKey}`,
                { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) }
            );
            const data = await res.json();
            return data.error
                ? { success: false, message: data.error.message }
                : { success: true, message: data.candidates?.[0]?.content?.parts?.[0]?.text || "Resposta recebida!" };

        } else if (providerId === "evolution") {
            const baseUrl = (config.apiUrl || "").replace(/\/$/, "");
            const instance = config.instanceName;

            const stateRes = await fetch(`${baseUrl}/instance/connectionState/${instance}`, {
                method: "GET",
                headers: { apikey: config.apiKey },
            });
            const stateData = await stateRes.json();

            if (!stateRes.ok) {
                return { success: false, message: IntegrationService.extractErrorMessage(stateData, "Erro ao verificar instância.") };
            }

            const state = stateData?.instance?.state || stateData?.state || "desconhecido";
            if (state !== "open") {
                return { success: false, message: `Instância "${instance}" não conectada (estado: "${state}"). Escaneie o QR Code.` };
            }

            if (!testInput || testInput.replace(/\D/g, "").length < 10) {
                return { success: true, message: `✅ Conectado! Instância "${instance}" está online.\n\nDigite um número para enviar uma mensagem de teste.` };
            }

            const number = testInput.replace(/\D/g, "");
            const msgRes = await fetch(`${baseUrl}/message/sendText/${instance}`, {
                method: "POST",
                headers: { "Content-Type": "application/json", apikey: config.apiKey },
                body: JSON.stringify({ number, text: "✅ Mensagem de teste do NeoApp — integração funcionando!" }),
            });
            const msgData = await msgRes.json();

            return msgRes.ok
                ? { success: true, message: `✅ Mensagem enviada com sucesso para ${number}!` }
                : { success: false, message: IntegrationService.extractErrorMessage(msgData, "Erro ao enviar mensagem.") };
        }
    },

    /**
     * Verifica o status da Evolution API.
     */
    checkEvolutionStatus: async (config) => {
        const baseUrl = config.apiUrl.replace(/\/$/, "");
        const res = await fetch(`${baseUrl}/instance/connectionState/${config.instanceName}`, {
            method: "GET",
            headers: { apikey: config.apiKey },
        });
        const data = await res.json();
        return { ok: res.ok, state: data?.instance?.state || data?.state || "error" };
    },

    /**
     * Envia uma mensagem via Evolution API
     * Formata o número (adiciona 55 se Brasil, remove não numéricos)
     */
    sendEvolutionMessage: async (config, phoneNumber, text) => {
        const baseUrl = (config.apiUrl || "").replace(/\/$/, "");
        const instance = config.instanceName;

        // Formatar número de telefone
        let phoneNum = (phoneNumber || "").replace(/\D/g, "");
        if (phoneNum.startsWith("0")) phoneNum = phoneNum.substring(1);
        if (phoneNum && !phoneNum.startsWith("55")) phoneNum = "55" + phoneNum;

        if (!phoneNum || phoneNum.length < 10) {
            return { success: false, reason: "invalid_phone" };
        }

        const msgRes = await fetch(`${baseUrl}/message/sendText/${instance}`, {
            method: "POST",
            headers: { "Content-Type": "application/json", apikey: config.apiKey },
            body: JSON.stringify({ number: phoneNum, text }),
        });

        if (!msgRes.ok) {
            const msgData = await msgRes.json();
            return { success: false, reason: IntegrationService.extractErrorMessage(msgData, "Erro ao enviar mensagem.") };
        }

        return { success: true };
    }
};

export default IntegrationService;
